import { FormFieldsData } from 'framework/form';
import useTimeTrackingService from './useTimeTrackingService';
import validation from './workloadReportingIssuesValidation';

const hasValidTicketId = (value?: string | null): boolean => {
  if (value === undefined || value === null) {
    return false;
  }

  const trimmed = String(value).trim();

  if (!trimmed || trimmed === '0') {
    return false;
  }

  return true;
};

interface WorkloadChangeItem {
  // Incorrect time entry fields
  ticketId?: string;
  trackingDate?: string;
  timeEntryId?: string;
  currentTimeTrack?: string;
  correctTicketId?: string | null;
  correctTrackingDate?: string | null;
  correctTimeTrack?: string | null;
  // Missed tracking fields
  activity?: string;
  descriptionOfWork?: string;
}

const useWorkloadReportingIssuesService = () => {
  const timeTrackingService = useTimeTrackingService();

  const validateTimeTrackingChanges = async (changes: WorkloadChangeItem[], requestType: string): Promise<void> => {
    const errors: Record<string, string[]> = {};
    const ticketTotalMessages: string[] = [];
    const ticketIds = new Set<number>();

    // Collect all ticket IDs from changes
    changes.forEach(change => {
      if (hasValidTicketId(change.ticketId)) {
        const parsed = Number(change.ticketId);
        if (!Number.isNaN(parsed)) {
          ticketIds.add(parsed);
        }
      }
      if (hasValidTicketId(change.correctTicketId)) {
        const parsed = Number(change.correctTicketId);
        if (!Number.isNaN(parsed)) {
          ticketIds.add(parsed);
        }
      }
    });

    // Track changes per ticket
    const ticketChanges: Record<number, number> = {};

    // First pass: validate required ticket fields, individual time tracks and calculate changes per ticket
    changes.forEach((change, index) => {
      const hasOriginalTicketId = hasValidTicketId(change.ticketId);
      const hasCorrectTicketId = hasValidTicketId(change.correctTicketId);

      if (!hasOriginalTicketId && !hasCorrectTicketId) {
        const errorKey = `changes[${index}].ticketId`;
        if (!errors[errorKey]) {
          errors[errorKey] = [];
        }
        errors[errorKey].push('Incorrect ticket.');
        return;
      }

      if (requestType === 'EXISTING_TIME_ADJUSTMENT') {
        const currentHours = change.currentTimeTrack ? parseFloat(change.currentTimeTrack) : 0;
        const correctHours = change.correctTimeTrack ? parseFloat(change.correctTimeTrack) : 0;
        const originalTicketId = change.ticketId ? Number(change.ticketId) : null;
        const correctTicketId = change.correctTicketId ? Number(change.correctTicketId) : null;

        // Check individual time track doesn't exceed 8 hours
        if (correctHours > 8) {
          const errorKey = `changes[${index}].correctTimeTrack`;
          if (!errors[errorKey]) {
            errors[errorKey] = [];
          }
          errors[errorKey].push('Time track cannot exceed 8 hours');
        }

        // Calculate changes: subtract from original ticket, add to correct ticket
        if (originalTicketId && correctTicketId) {
          // Ticket ID changed: subtract from original, add to correct
          ticketChanges[originalTicketId] = (ticketChanges[originalTicketId] || 0) - currentHours;
          ticketChanges[correctTicketId] = (ticketChanges[correctTicketId] || 0) + correctHours;
        } else if (originalTicketId) {
          // Same ticket: just adjust the hours
          ticketChanges[originalTicketId] = (ticketChanges[originalTicketId] || 0) + (correctHours - currentHours);
        } else if (correctTicketId) {
          // Only correct ticket (shouldn't happen, but handle it)
          ticketChanges[correctTicketId] = (ticketChanges[correctTicketId] || 0) + correctHours;
        }
      } else if (requestType === 'NEW_TIME_ENTRY') {
        const timeTrackHours = change.correctTimeTrack ? parseFloat(change.correctTimeTrack) : 0;
        const correctTicketId = change.correctTicketId ? Number(change.correctTicketId) : null;

        // Check individual time track doesn't exceed 8 hours
        if (timeTrackHours > 8) {
          const errorKey = `changes[${index}].correctTimeTrack`;
          if (!errors[errorKey]) {
            errors[errorKey] = [];
          }
          errors[errorKey].push('Time track cannot exceed 8 hours');
        }

        // Add to correct ticket
        if (correctTicketId) {
          ticketChanges[correctTicketId] = (ticketChanges[correctTicketId] || 0) + timeTrackHours;
        }
      }
    });

    // Second pass: validate total hours for each ticket
    for (const ticketId of ticketIds) {
      try {
        // Get ticket information and time entries
        const [issue, timeTracks] = await Promise.all([
          timeTrackingService.getIssue(ticketId),
          timeTrackingService.getTimeTracksForIssue(ticketId),
        ]);

        // Calculate current total logged time
        const currentTotalHours = timeTracks.reduce((sum, track) => sum + (track.hours || 0), 0);

        // Calculate total after changes
        const changeHours = ticketChanges[ticketId] || 0;
        const totalAfterChanges = currentTotalHours + changeHours;

        // Validate total hours
        if (issue?.pessimistic_estimated_hours) {
          if (totalAfterChanges > issue.pessimistic_estimated_hours) {
            ticketTotalMessages.push(
              `Total logged time for ticket #${ticketId} after changes (${totalAfterChanges.toFixed(2)} h) exceeds pessimistic estimate (${issue.pessimistic_estimated_hours} h)`,
            );
          }
        } else if (issue?.estimated_hours) {
          if (totalAfterChanges > issue.estimated_hours) {
            ticketTotalMessages.push(
              `Total logged time for ticket #${ticketId} after changes (${totalAfterChanges.toFixed(2)} h) exceeds estimated hours (${issue.estimated_hours} h)`,
            );
          }
        } else {
          if (totalAfterChanges > 4) {
            ticketTotalMessages.push(
              `Total logged time for ticket #${ticketId} after changes (${totalAfterChanges.toFixed(2)} h) exceeds 4 hours (estimated hours not set)`,
            );
          }
        }
      } catch (error) {
        // If we can't fetch ticket data, skip validation for this ticket
        // Error is silently handled to avoid blocking validation for other tickets
      }
    }

    if (Object.keys(errors).length === 0 && ticketTotalMessages.length > 0) {
      throw ticketTotalMessages.join('\n');
    }

    if (Object.keys(errors).length > 0) {
      const body: { errors: Record<string, string[]>; message?: string } = { errors };
      if (ticketTotalMessages.length > 0) {
        body.message = ticketTotalMessages.join('\n');
      }
      throw new Response(JSON.stringify(body), {
        status: 400,
        statusText: 'Validation failed',
        headers: { 'Content-Type': 'application/json' },
      });
    }
  };

  const createRequest = async (data: FormFieldsData) => {
    const transformChangeItem = (item: any): any => {
      const transformed: any = { ...item };

      // Convert timeEntryId to integer
      if (transformed.timeEntryId !== undefined && transformed.timeEntryId !== null && transformed.timeEntryId !== '') {
        const parsed = parseInt(transformed.timeEntryId, 10);
        if (!isNaN(parsed)) {
          transformed.timeEntryId = parsed;
        }
      }

      // Convert currentTimeTrack to double
      if (
        transformed.currentTimeTrack !== undefined &&
        transformed.currentTimeTrack !== null &&
        transformed.currentTimeTrack !== ''
      ) {
        const parsed = parseFloat(transformed.currentTimeTrack);
        if (!isNaN(parsed)) {
          transformed.currentTimeTrack = parsed;
        }
      }

      // Convert correctTimeTrack to double
      if (
        transformed.correctTimeTrack !== undefined &&
        transformed.correctTimeTrack !== null &&
        transformed.correctTimeTrack !== ''
      ) {
        const parsed = parseFloat(transformed.correctTimeTrack);
        if (!isNaN(parsed)) {
          transformed.correctTimeTrack = parsed;
        }
      }

      return transformed;
    };

    const transformedChanges = ((data.changes || []) as WorkloadChangeItem[]).map(transformChangeItem);

    await validateTimeTrackingChanges(transformedChanges, data.requestType);

    const requestData = {
      requestType: data.requestType,
      reasonForChange: data.reasonForChange,
      changes: transformedChanges,
    };

    await timeTrackingService.createChangeRequest(requestData);
  };

  const getValidationSchema = () => Promise.resolve(validation);

  return { createRequest, getValidationSchema };
};

export default useWorkloadReportingIssuesService;
